import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, UserPlus, KeyRound } from "lucide-react";

interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: string;
  createdAt: string;
}

export default function UserManager() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState<{ userId: number; password: string } | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const data = await apiFetch("/users");
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiFetch("/users", {
        method: "POST",
        body: JSON.stringify({ email, password, name, role: "manager" }),
      });
      await loadUsers();
      setShowAdd(false);
      setEmail("");
      setPassword("");
      setName("");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Are you sure you want to remove this team member?")) return;
    try {
      await apiFetch(`/users/${id}`, { method: "DELETE" });
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function handleResetPassword(id: number, userName: string) {
    if (!confirm(`Reset password for ${userName}? A temporary password will be generated.`)) return;
    try {
      const data = await apiFetch(`/users/${id}/reset-password`, { method: "PUT" });
      setTempPassword({ userId: id, password: data.tempPassword });
    } catch (err: any) {
      setError(err.message);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-serif font-bold">Team Members</h2>
        <Button onClick={() => setShowAdd(!showAdd)} style={{ borderRadius: "16px 0 16px 0" }}>
          <UserPlus className="w-4 h-4 mr-1" /> Add Member
        </Button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-md">{error}</div>}

      {showAdd && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h3 className="font-semibold mb-4">Add a New Manager</h3>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <Label htmlFor="memberName">Full Name</Label>
              <Input id="memberName" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="memberEmail">Email</Label>
              <Input id="memberEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="memberPassword">Password</Label>
              <Input id="memberPassword" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} style={{ borderRadius: "16px 0 16px 0" }}>
                {saving ? "Adding..." : "Add Manager"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading team members...</p>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{u.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "owner" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                      {u.role}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{u.email}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleResetPassword(u.id, u.name)} className="p-2 hover:bg-blue-50 text-blue-500 rounded-md" title="Reset Password">
                    <KeyRound className="w-4 h-4" />
                  </button>
                  {u.role !== "owner" && (
                    <button onClick={() => handleDelete(u.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-md" title="Remove">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {tempPassword && tempPassword.userId === u.id && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-sm font-medium text-amber-800">Temporary password:</p>
                  <p className="text-sm font-mono bg-white px-2 py-1 mt-1 rounded border select-all">{tempPassword.password}</p>
                  <p className="text-xs text-amber-600 mt-1">Copy this and send it to {u.name}. They should change it after logging in.</p>
                  <button onClick={() => setTempPassword(null)} className="text-xs text-amber-700 underline mt-1">Dismiss</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
